/**
 * Central League Registry
 *
 * THE single source of truth for all supported leagues/sports.
 * Add a new sport here and every app picks it up automatically.
 *
 * When adding a new sport:
 *   1. Add the code to SportCode union
 *   2. Add the entry to SUPPORTED_SPORTS
 */

/** Union of all supported sport codes */
export type SportCode =
  // League codes. Their artwork is the LEAGUE's mark, so only a client with the rights to it
  // — in practice one with a licensed feed — should declare these.
  | 'NFL'
  | 'NBA'
  | 'NCAAMB'
  | 'NCAAFB'
  | 'MLB'
  | 'PGA'
  // Sport codes. These name the SPORT, not a competition, and their artwork is generic — a
  // football, a bat and ball. Any client may declare them, and a client that declares only
  // these cannot surface a trademark, in its labels or its images. That is the point: a
  // feed-less client still wants Sport as an identifier on a Contest, and this is how it gets
  // one honestly.
  | 'SOCCER'
  | 'FOOTBALL'
  | 'BASEBALL'
  | 'BASKETBALL'
  | 'HOCKEY'
  | 'CRICKET';

export interface LeagueConfig {
  /** Lowercase league identifier, e.g. 'nfl' */
  id: string;
  /** Full league name, e.g. 'National Football League' */
  name: string;
  /** Uppercase sport code, e.g. 'NFL' */
  code: SportCode;
  /** Path to league icon (for admin list pages) */
  iconSrc?: string;
  /** Label shown in filter dropdowns */
  filterLabel?: string;
  /**
   * Name of the icon component to render in the admin Sports sidebar.
   * Matches a key in `apps/admin/src/assets/Icon.tsx` so consumers can
   * iterate SUPPORTED_SPORTS instead of hardcoding the list of links.
   */
  sidebarIconName?: string;
  /** Label shown in the admin sidebar (defaults to filterLabel/name). */
  sidebarLabel?: string;
}

export const SUPPORTED_SPORTS: readonly LeagueConfig[] = [
  // ── Sport codes: generic artwork, plain-English labels, no trademark ──────────────────
  { id: 'football', name: 'Football', code: 'FOOTBALL', iconSrc: '/american-football-logo.svg', filterLabel: 'Football', sidebarIconName: 'Football', sidebarLabel: 'Football' },
  { id: 'basketball', name: 'Basketball', code: 'BASKETBALL', iconSrc: '/basketball-logo.svg', filterLabel: 'Basketball', sidebarIconName: 'Basketball', sidebarLabel: 'Basketball' },
  { id: 'baseball', name: 'Baseball', code: 'BASEBALL', iconSrc: '/baseball-logo.svg', filterLabel: 'Baseball', sidebarLabel: 'Baseball' },
  { id: 'ice-hockey', name: 'Ice Hockey', code: 'HOCKEY', iconSrc: '/ice-hockey-logo.svg', filterLabel: 'Ice Hockey', sidebarLabel: 'Ice Hockey' },
  { id: 'cricket', name: 'Cricket', code: 'CRICKET', iconSrc: '/cricket-logo.svg', filterLabel: 'Cricket', sidebarLabel: 'Cricket' },
  // ── League codes: the league's own mark, for clients holding the rights ───────────────
  { id: 'nba', name: 'National Basketball Association', code: 'NBA', iconSrc: '/nba-logo.svg', filterLabel: 'NBA', sidebarIconName: 'Basketball', sidebarLabel: 'NBA' },
  { id: 'nfl', name: 'National Football League', code: 'NFL', iconSrc: '/league-icon.svg', filterLabel: 'NFL', sidebarIconName: 'Football', sidebarLabel: 'NFL' },
  { id: 'ncaamb', name: "NCAA Men's Basketball", code: 'NCAAMB', iconSrc: '/basketball-logo.svg', filterLabel: 'NCAAB', sidebarIconName: 'Basketball', sidebarLabel: 'NCAA Basketball' },
  { id: 'ncaafb', name: 'NCAA Football', code: 'NCAAFB', iconSrc: '/american-football-logo.svg', filterLabel: 'NCAAF', sidebarIconName: 'Football', sidebarLabel: 'NCAA Football' },
  { id: 'mlb', name: 'Major League Baseball', code: 'MLB', iconSrc: '/league-icon.svg', filterLabel: 'MLB', sidebarIconName: 'Baseball', sidebarLabel: 'MLB' },
  { id: 'pga', name: 'PGA Tour', code: 'PGA', iconSrc: '/pga-logo.svg', filterLabel: 'PGA', sidebarIconName: 'Golf', sidebarLabel: 'PGA' },
  // Soccer is a single sport that internally covers multiple competitions
  // (Premier League, FIFA World Cup, etc.). Feed-service holds the list of
  // active Sportradar competition IDs and merges fixtures across them.
  { id: 'soccer', name: 'Soccer', code: 'SOCCER', iconSrc: '/soccer-logo.svg', filterLabel: 'Soccer', sidebarIconName: 'Soccer', sidebarLabel: 'Soccer' },
];

/** e.g. ['nfl', 'nba', 'ncaamb', 'mlb'] */
export const SUPPORTED_LEAGUE_IDS: readonly string[] = SUPPORTED_SPORTS.map(l => l.id);

/** e.g. ['NFL', 'NBA', 'NCAAMB', 'MLB'] */
export const SUPPORTED_SPORT_CODES: readonly string[] = SUPPORTED_SPORTS.map(l => l.code);

/** Map from uppercase sport code to lowercase league ID, e.g. { NFL: 'nfl', NBA: 'nba', ... } */
export const SPORT_TO_LEAGUE_ID: Record<string, string> = Object.fromEntries(
  SUPPORTED_SPORTS.map(l => [l.code, l.id])
);

/** Formatted list for error messages, e.g. '"nfl", "nba", "ncaamb", or "mlb"' */
export const LEAGUE_ID_LIST: string = SUPPORTED_LEAGUE_IDS.map(id => `"${id}"`).join(', ');

/** Runtime validation helper */
export function isValidLeagueId(id: string): boolean {
  return SUPPORTED_LEAGUE_IDS.includes(id.toLowerCase());
}

/** Sports that are individual (not team-based) */
export const INDIVIDUAL_SPORTS: readonly SportCode[] = ['PGA'];

/** Check if a sport is individual (not team-based) */
export function isIndividualSport(sport: string): boolean {
  return INDIVIDUAL_SPORTS.includes(sport.toUpperCase() as SportCode);
}
